import { AlertTriangle, CheckCircle2, GitMerge, Info } from "lucide-react";
import { type ReactNode, useState } from "react";
import { useNavigate } from "react-router-dom";

import type { SuggestedMatch, UnmatchedRecipient } from "@busright/data-contract";

import { ChartCard } from "@/components/ChartCard";
import { DataTable } from "@/components/DataTable";
import { EmptyState } from "@/components/EmptyState";
import { KpiCard } from "@/components/KpiCard";
import { PageHeader } from "@/components/PageHeader";
import { SkeletonLines } from "@/components/Skeleton";
import { useMatchOverride, useSuggestedMatches, useUnmatchedRecipients } from "@/hooks/useMatch";
import { formatInt } from "@/lib/format";
import { isSyntheticRecipient } from "@/lib/names";

// =================================================================================================
// Reconciliation — the actionable account-match surface behind the "217/278/495 unmatched (DQ)"
// number. An "unmatched recipient" is an E2 order-recipient name the matcher could not resolve to a
// warehouse district_id (or Linear account) by exact/fuzzy join — so its hardware/orders/health never
// roll up under a real district. This page lists the backlog, shows suggested matches with
// confidence for a selected row, and lets a human confirm or override the mapping.
//
// Human-in-the-loop only: nothing here auto-applies a match. Confidence is a FACT (0..1), not a
// health score, so it is never RAG-colored — rendered as plain text/percent, tiered by weight only.
// In snapshot mode /api/match/override just echoes the input back (no real persistence); we show an
// optimistic success with an explicit "not yet persisted" note rather than overclaiming a write.
// =================================================================================================

const HIGH_CONFIDENCE_FLOOR = 0.75;
const MEDIUM_CONFIDENCE_FLOOR = 0.45;

const CONFIDENCE_TIER = (confidence: number): "High" | "Medium" | "Low" => {
  if (confidence >= HIGH_CONFIDENCE_FLOOR) {
    return "High";
  }
  if (confidence >= MEDIUM_CONFIDENCE_FLOOR) {
    return "Medium";
  }
  return "Low";
};

const METHOD_LABEL: Record<SuggestedMatch["method"], string> = {
  exact: "Exact key match",
  linear: "Linear account",
  name: "Fuzzy name (+state)",
  time_recipient: "Send-date + recipient",
  description_sku: "Parsed description/SKU",
  geocode: "Address geocode",
};

const CLASSIFICATION_LABEL: Record<UnmatchedRecipient["classification"], string> = {
  district: "District",
  customer: "Customer",
  one_off: "One-off",
  pilot: "Pilot",
  internal: "BusRight internal",
};

// Confidence tier → text color. Weight only, never a RAG color — confidence is a fact, not a score.
const CONFIDENCE_TIER_COLOR: Record<ReturnType<typeof CONFIDENCE_TIER>, string> = {
  High: "var(--br-text-primary)",
  Medium: "var(--br-text-secondary)",
  Low: "var(--br-text-muted)",
};

const PCT_MULTIPLIER = 100;
const PENDING_OTHER_ROW_OPACITY = 0.5;
const FULL_OPACITY = 1;

// The suggested-match picker + confirm/override action for one selected unmatched recipient.
const MatchPicker = ({
  recipient,
  onConfirmed,
}: {
  readonly recipient: UnmatchedRecipient;
  readonly onConfirmed: (result: {
    readonly districtId: string;
    readonly displayName: string;
  }) => void;
}): ReactNode => {
  const { data, isLoading, isError, error } = useSuggestedMatches(recipient.id);
  const override = useMatchOverride();
  const suggestions = data?.data.suggestions ?? [];

  const handleConfirm = (candidateId: string): void => {
    override.mutate(
      { recipientId: recipient.id, districtId: candidateId },
      {
        onSuccess: (res) => onConfirmed(res.data),
      },
    );
  };

  if (isLoading) {
    return <SkeletonLines count={3} />;
  }

  if (isError) {
    return (
      <EmptyState
        title="Couldn't load suggestions"
        message={
          error instanceof Error
            ? `${error.message} — /api/match/suggest may not be live yet.`
            : "/api/match/suggest may not be live yet."
        }
        icon={AlertTriangle}
      />
    );
  }

  if (suggestions.length === 0) {
    return (
      <EmptyState
        title="No candidate matches"
        message="The matcher found no plausible district/account for this recipient's raw name."
      />
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      {suggestions.map((s) => {
        const tier = CONFIDENCE_TIER(s.confidence);
        const isPendingThis =
          override.isPending && override.variables?.districtId === s.candidateId;
        return (
          <div
            key={s.candidateId}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 12,
              padding: "10px 12px",
              borderRadius: "var(--br-radius-sm)",
              border: "1px solid var(--br-border)",
              background: "var(--br-surface-2)",
            }}
          >
            <div style={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 0 }}>
              <span style={{ color: "var(--br-text-primary)", fontWeight: 600 }}>
                {s.candidateName}
              </span>
              <span style={{ color: "var(--br-text-muted)", fontSize: 12 }}>
                {METHOD_LABEL[s.method]} · id: {s.candidateId}
              </span>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 10, flexShrink: 0 }}>
              <span
                className="br-tabular-nums"
                style={{
                  fontSize: 12,
                  color: CONFIDENCE_TIER_COLOR[tier],
                }}
                title="Match confidence — a fact, not a health score; never RAG-colored"
              >
                {tier} · {(s.confidence * PCT_MULTIPLIER).toFixed(0)}%
              </span>
              <button
                type="button"
                disabled={override.isPending}
                onClick={() => handleConfirm(s.candidateId)}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  padding: "6px 10px",
                  borderRadius: "var(--br-radius-sm)",
                  border: "1px solid var(--br-border-strong)",
                  background: "var(--br-accent)",
                  color: "var(--br-accent-contrast)",
                  fontSize: 12,
                  fontWeight: 600,
                  cursor: override.isPending ? "default" : "pointer",
                  opacity:
                    override.isPending && !isPendingThis ? PENDING_OTHER_ROW_OPACITY : FULL_OPACITY,
                }}
              >
                <CheckCircle2 size={13} aria-hidden />
                {isPendingThis ? "Confirming…" : "Confirm match"}
              </button>
            </div>
          </div>
        );
      })}
      {override.isError ? (
        <p style={{ color: "var(--br-rag-critical)", fontSize: 12 }}>
          {override.error instanceof Error
            ? override.error.message
            : "Override failed — /api/match/override may not be live yet."}
        </p>
      ) : null}
    </div>
  );
};

export const ReconciliationPage = (): ReactNode => {
  const navigate = useNavigate();
  const { data, isLoading, isError, error } = useUnmatchedRecipients();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [confirmedNote, setConfirmedNote] = useState<{
    readonly recipientId: string;
    readonly districtId: string;
    readonly displayName: string;
  } | null>(null);

  const payload = data?.data;
  const meta = data?.meta;
  const allRows = payload?.rows ?? [];
  // Hide synthetic/internal/demo rows from the DQ worklist — they are not real match-backlog.
  const rows = allRows.filter((r) => !isSyntheticRecipient(r.rawName));
  const hiddenSyntheticCount = allRows.length - rows.length;
  const selected = rows.find((r) => r.id === selectedId) ?? null;

  if (isError) {
    return (
      <>
        <PageHeader title="Reconciliation" meta={meta} />
        <ChartCard title="Unmatched recipients">
          <EmptyState
            title="Couldn't load the unmatched-recipient backlog"
            message={
              error instanceof Error
                ? `${error.message} — /api/match/unmatched may not be live yet.`
                : "/api/match/unmatched may not be live yet."
            }
            icon={AlertTriangle}
          />
        </ChartCard>
      </>
    );
  }

  return (
    <>
      <PageHeader
        title="Reconciliation"
        description="An unmatched recipient is an E2 order-recipient name the matcher couldn't resolve to a real district — its hardware, orders, and health never roll up. Pick a row, review suggested matches, and confirm or override the mapping."
        meta={meta}
      />

      <div className="grid kpi-band">
        <KpiCard
          label="Unmatched recipients"
          icon={GitMerge}
          loading={isLoading}
          value={payload ? formatInt(rows.length) : "—"}
          source="account-match engine (/api/match/unmatched)"
        />
        <KpiCard
          label="Hidden synthetic/internal rows"
          icon={Info}
          loading={isLoading}
          value={payload ? formatInt(hiddenSyntheticCount) : "—"}
          source="test/demo/BusRight-internal rows filtered from the DQ worklist"
        />
      </div>

      {confirmedNote ? (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            padding: "10px 14px",
            marginTop: 4,
            borderRadius: "var(--br-radius-sm)",
            border: "1px solid var(--br-border-strong)",
            background: "var(--br-surface-2)",
            color: "var(--br-text-secondary)",
            fontSize: 13,
          }}
        >
          <CheckCircle2 size={15} aria-hidden style={{ color: "var(--br-rag-ok)" }} />
          Matched recipient {confirmedNote.recipientId} → {confirmedNote.displayName} (
          {confirmedNote.districtId}). In snapshot mode this echoes the input and is not yet
          persisted — the write path is live once the matcher's mapping table is wired.
        </div>
      ) : null}

      <div className="grid cols-2" style={{ marginTop: 16, alignItems: "start" }}>
        <ChartCard
          title="Unmatched backlog"
          subtitle="Click a row to review suggested matches"
          source="account-match engine"
          loading={isLoading}
        >
          <DataTable
            columns={[
              {
                key: "rawName",
                header: "Raw E2 name",
                render: (r: UnmatchedRecipient) => r.rawName,
              },
              {
                key: "classification",
                header: "Classification",
                render: (r: UnmatchedRecipient) => CLASSIFICATION_LABEL[r.classification],
              },
              {
                key: "id",
                header: "Recipient id",
                align: "right",
                render: (r: UnmatchedRecipient) => <span className="br-tabular-nums">{r.id}</span>,
              },
            ]}
            rows={rows}
            rowKey={(r) => r.id}
            onRowClick={(r) => {
              setSelectedId(r.id);
              setConfirmedNote(null);
            }}
            emptyTitle="No unmatched recipients"
            emptyMessage="The account-match backlog is empty — every recipient resolved to a district."
          />
        </ChartCard>

        <ChartCard
          title={selected ? `Suggested matches — ${selected.rawName}` : "Suggested matches"}
          subtitle={
            selected
              ? "Confidence is a fact, not a health score — never RAG-colored. Confirming persists the mapping."
              : "Select a recipient from the backlog to see candidates"
          }
          source="account-match engine (/api/match/suggest)"
        >
          {selected ? (
            <MatchPicker
              recipient={selected}
              onConfirmed={(res) => {
                setConfirmedNote({ recipientId: selected.id, ...res });
                navigate(`/recipient/${encodeURIComponent(res.districtId)}`);
              }}
            />
          ) : (
            <EmptyState
              title="No recipient selected"
              message="Pick a row on the left to trace its name mismatch and see candidate districts."
            />
          )}
        </ChartCard>
      </div>
    </>
  );
};
